/* Aggregate a fine-grained value/NAV series into OHLC candles.

   A portfolio equity curve is one value per instant, not open/high/low/close.
   To candlestick it, bucket the points by period (e.g. calendar day) and take
   open = first point in the bucket, high = max, low = min, close = last. This is
   the same primitive the real pipeline uses on live equity points.

   Pure and immutable: the input array is never mutated and fresh objects are
   returned. Buckets are emitted in first-seen order, so a chronological input
   yields chronological candles. */

export type NavPoint = { time: string; value: number };
export type Ohlc = { time: string; open: number; high: number; low: number; close: number };

export function aggregateNavToOhlc(
  navPoints: readonly NavPoint[],
  bucketKeyFn: (point: NavPoint, index: number) => string,
): Ohlc[] {
  const order: string[] = [];
  const buckets = new Map<string, NavPoint[]>();

  navPoints.forEach((point, index) => {
    const key = bucketKeyFn(point, index);
    const existing = buckets.get(key);
    if (existing) {
      existing.push(point);
    } else {
      buckets.set(key, [point]);
      order.push(key);
    }
  });

  return order.map((key) => {
    const points = buckets.get(key)!;
    const values = points.map((p) => p.value);
    return {
      time: key,
      open: points[0].value,
      high: Math.max(...values),
      low: Math.min(...values),
      close: points[points.length - 1].value,
    };
  });
}
